import { useState } from "react";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useChangeMyPasswordMutation } from "@/store/api";
import { useAppDispatch } from "@/store";
import { logout } from "@/store/authSlice";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

interface Props {
  open: boolean;
  onClose: () => void;
}

/** Modal for the logged-in user to change their own password. On
 *  success the SPA force-logs-out (the backend's iat-vs-changed-at
 *  check has already invalidated this JWT). */
export function ChangePasswordDialog({ open, onClose }: Props) {
  const [changePassword, { isLoading }] = useChangeMyPasswordMutation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");

  const reset = () => {
    setCurrentPw("");
    setNewPw("");
    setConfirmPw("");
  };

  const disabled =
    isLoading || !currentPw || newPw.length < 8 || newPw !== confirmPw;

  const submit = async () => {
    if (newPw !== confirmPw) {
      toast.error("New passwords don't match");
      return;
    }
    try {
      await changePassword({
        current_password: currentPw,
        new_password: newPw,
      }).unwrap();
      toast.success("Password changed — sign in again");
      reset();
      onClose();
      dispatch(logout());
      navigate("/login");
    } catch (err: unknown) {
      const detail =
        (err as { data?: { detail?: string } })?.data?.detail
        || "Password change failed";
      toast.error(detail);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => { reset(); onClose(); }}
      size="md"
      title="Change password"
      description="Updating your password signs you out — log back in with the new password."
    >
      <div className="space-y-3">
        <div className="space-y-1">
          <Label htmlFor="pw-current">Current password</Label>
          <Input
            id="pw-current"
            type="password"
            autoComplete="current-password"
            value={currentPw}
            onChange={(e) => setCurrentPw(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="pw-new">New password</Label>
          <Input
            id="pw-new"
            type="password"
            autoComplete="new-password"
            value={newPw}
            onChange={(e) => setNewPw(e.target.value)}
          />
          <div className="text-xs text-muted">Minimum 8 characters.</div>
        </div>
        <div className="space-y-1">
          <Label htmlFor="pw-confirm">Confirm new password</Label>
          <Input
            id="pw-confirm"
            type="password"
            autoComplete="new-password"
            value={confirmPw}
            onChange={(e) => setConfirmPw(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !disabled) submit(); }}
          />
          {confirmPw.length > 0 && newPw !== confirmPw && (
            <div className="text-xs text-bad">Passwords don't match.</div>
          )}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" size="sm" onClick={() => { reset(); onClose(); }}>
            Cancel
          </Button>
          <Button size="sm" disabled={disabled} onClick={submit}>
            {isLoading ? "Saving…" : "Change password"}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
